import { BALANCE } from '@2k48/shared';
import { NodePool } from './NodePool';
import { metrics } from './debug/metrics';
import { dur, gsap, prefersReducedMotion } from './motion';

export type CombatSide = 'p1' | 'p2';

export interface AttackEffectOptions {
  attacker: CombatSide;
  target: CombatSide;
  damage: number;
  hpFrom: number;
  hpTo: number;
  sourceTileId?: string;
  powerValue?: 512 | 1024 | 2048;
  onCharge?(): void;
  onImpact?(): void;
  onHpCommit?(): void;
  onComplete?(): void;
}

export interface HitEffectOptions {
  target: CombatSide;
  damage: number;
  hpFrom: number;
  hpTo: number;
  onImpact?(): void;
  onHpCommit?(): void;
  onComplete?(): void;
}

interface CombatEffectNode extends HTMLElement {
  beam: HTMLElement;
  impact: HTMLElement;
  damage: HTMLElement;
  ko: HTMLElement;
}

export class CombatEffects {
  readonly root: HTMLElement;
  private readonly pool: NodePool<CombatEffectNode>;
  private current: gsap.core.Timeline | null = null;
  private destroying = false;

  constructor(
    private readonly view: HTMLElement,
    private readonly board: HTMLElement,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'combat-fx-layer';
    view.appendChild(this.root);
    this.pool = new NodePool(
      () => createEffectNode(),
      this.root,
      BALANCE.perf.combatEffectPoolSize,
      'combat-effects',
    );
    this.reportPool();
  }

  playAttack(options: AttackEffectOptions): gsap.core.Timeline {
    this.finishRunning();
    const node = this.pool.acquire();
    this.reportPool();
    prepareNode(node, options.attacker, options.powerValue);

    const source = this.sourceNode(options.sourceTileId);
    const target = this.targetPanel(options.target);
    // An opponent's board is not rendered locally. In that case the beam
    // originates at their HUD panel instead of charging an unrelated local
    // tile that happens to share the same entity id.
    const origin = source ?? this.targetPanel(options.attacker);
    const geometry = this.geometry(origin, target);
    const avatarUse = target?.querySelector<SVGUseElement>('.avatar use') ?? null;
    const previousAvatar = avatarUse?.getAttribute('href') ?? null;
    const sourceGlow = source?.querySelector<HTMLElement>('.entity__glow') ?? null;

    this.place(node, geometry);
    node.damage.textContent = `-${Math.max(0, Math.floor(options.damage))}`;

    const chargeSec = dur(0.2);
    const travelSec = dur(0.15);
    const impactSec = dur(0.12);
    const drainSec = dur(0.3);
    const impactAt = chargeSec + travelSec;
    const drainAt = impactAt + impactSec;

    const cleanup = (): void => {
      source?.classList.remove('entity--charged');
      target?.classList.remove('hurt');
      if (avatarUse && previousAvatar) avatarUse.setAttribute('href', previousAvatar);
      this.clearSegments(target);
      resetEffectNode(node);
      this.pool.release(node);
      this.reportPool();
      if (this.current === timeline) this.current = null;
      if (!this.destroying) options.onComplete?.();
    };

    const timeline = gsap.timeline({ onComplete: cleanup });
    this.current = timeline;

    timeline.call(
      () => {
        source?.classList.add('entity--charged');
        options.onCharge?.();
      },
      undefined,
      0,
    );
    if (source) {
      timeline.to(source, { scale: 1.1, duration: chargeSec, ease: 'power2.out' }, 0);
      timeline.to(source, { scale: 1, duration: dur(0.08), ease: 'power1.out' }, impactAt);
    }
    if (sourceGlow) {
      timeline.to(sourceGlow, { opacity: 1, duration: chargeSec }, 0);
      timeline.to(sourceGlow, { opacity: 0, duration: dur(0.08) }, impactAt);
    }

    timeline.set(node.beam, { opacity: 1, scaleY: 0 }, chargeSec);
    timeline.to(node.beam, { scaleY: 1, duration: travelSec, ease: 'power2.out' }, chargeSec);
    timeline.to(node.beam, { opacity: 0, duration: impactSec }, impactAt);

    this.addImpact(
      timeline,
      node,
      target,
      avatarUse,
      impactAt,
      Boolean(options.powerValue),
      options.onImpact,
    );
    this.addDrain(
      timeline,
      target,
      options.hpFrom,
      options.hpTo,
      drainAt,
      drainSec,
      options.onHpCommit,
    );

    timeline.set(node.damage, { opacity: 1, scale: 1 }, impactAt);
    timeline.to(
      node.damage,
      { opacity: 0, y: `-=${60 * this.unit()}`, duration: dur(0.5), ease: 'power1.out' },
      impactAt,
    );

    return timeline;
  }

  playHit(options: HitEffectOptions): gsap.core.Timeline {
    this.finishRunning();
    const node = this.pool.acquire();
    this.reportPool();
    prepareNode(node, options.target === 'p1' ? 'p2' : 'p1');
    const target = this.targetPanel(options.target);
    const geometry = this.geometry(null, target);
    this.place(node, geometry);
    node.damage.textContent = `-${Math.max(0, Math.floor(options.damage))}`;

    const avatarUse = target?.querySelector<SVGUseElement>('.avatar use') ?? null;
    const previousAvatar = avatarUse?.getAttribute('href') ?? null;
    const impactSec = dur(0.12);
    const drainSec = dur(0.3);
    const cleanup = (): void => {
      target?.classList.remove('hurt');
      if (avatarUse && previousAvatar) avatarUse.setAttribute('href', previousAvatar);
      this.clearSegments(target);
      resetEffectNode(node);
      this.pool.release(node);
      this.reportPool();
      if (this.current === timeline) this.current = null;
      if (!this.destroying) options.onComplete?.();
    };
    const timeline = gsap.timeline({ onComplete: cleanup });
    this.current = timeline;
    this.addImpact(timeline, node, target, avatarUse, 0, false, options.onImpact);
    this.addDrain(
      timeline,
      target,
      options.hpFrom,
      options.hpTo,
      impactSec,
      drainSec,
      options.onHpCommit,
    );
    timeline.set(node.damage, { opacity: 1 }, 0);
    timeline.to(node.damage, { opacity: 0, y: `-=${60 * this.unit()}`, duration: dur(0.5) }, 0);
    return timeline;
  }

  playKo(
    targetSide: CombatSide,
    onComplete?: () => void,
    onImpact?: () => void,
  ): gsap.core.Timeline {
    this.finishRunning();
    const node = this.pool.acquire();
    this.reportPool();
    prepareNode(node, targetSide === 'p1' ? 'p2' : 'p1');
    const target = this.targetPanel(targetSide);
    const cleanup = (): void => {
      target?.classList.remove('hurt');
      resetEffectNode(node);
      this.pool.release(node);
      this.reportPool();
      if (this.current === timeline) this.current = null;
      if (!this.destroying) onComplete?.();
    };
    const timeline = gsap.timeline({ onComplete: cleanup });
    this.current = timeline;
    timeline.call(() => {
      target?.classList.add('hurt');
      onImpact?.();
    });
    timeline.set(node.ko, { opacity: 1, scale: 0 }, 0);
    timeline.to(node.ko, { scale: 1.2, duration: dur(0.22), ease: 'back.out(3)' }, 0);
    timeline.to(node.ko, { scale: 1, duration: dur(0.12) });
    timeline.to(node.ko, { opacity: 0, duration: dur(0.35) }, `+=${dur(0.65)}`);
    if (!prefersReducedMotion()) {
      timeline.to(
        this.view,
        { keyframes: { x: [-6, 6, -4, 3, 0] }, duration: dur(0.2), ease: 'power2.out' },
        0,
      );
    }
    return timeline;
  }

  finishRunning(): void {
    // Completion callbacks may start the next beat (HP penalty -> K.O.). Drain
    // that short chain too, without losing the new timeline reference.
    for (let guard = 0; this.current && guard < 4; guard += 1) {
      const running = this.current;
      this.current = null;
      running.progress(1).kill();
    }
  }

  destroy(): void {
    this.destroying = true;
    this.finishRunning();
    this.pool.destroy();
    this.root.remove();
  }

  private addImpact(
    timeline: gsap.core.Timeline,
    node: CombatEffectNode,
    target: HTMLElement | null,
    avatarUse: SVGUseElement | null,
    at: number,
    heavy: boolean,
    onImpact?: () => void,
  ): void {
    timeline.call(
      () => {
        target?.classList.add('hurt');
        avatarUse?.setAttribute('href', '#av-hit');
        onImpact?.();
      },
      undefined,
      at,
    );
    timeline.set(node.impact, { opacity: 1, scale: 0.35 }, at);
    timeline.to(
      node.impact,
      { opacity: 0, scale: heavy ? 2.2 : 1.6, duration: dur(0.12), ease: 'power2.out' },
      at,
    );
    if (!prefersReducedMotion()) {
      const shake = heavy ? 6 : 4;
      timeline.to(
        this.view,
        {
          keyframes: { x: [-shake, shake, -shake * 0.55, shake * 0.3, 0] },
          duration: dur(0.12),
          ease: 'power2.out',
        },
        at,
      );
    }
  }

  private addDrain(
    timeline: gsap.core.Timeline,
    panel: HTMLElement | null,
    hpFrom: number,
    hpTo: number,
    at: number,
    duration: number,
    commit?: () => void,
  ): void {
    const segments = panel ? [...panel.querySelectorAll<HTMLElement>('.hpJ > i')] : [];
    const oldFilled = hpSegments(hpFrom, segments.length);
    const newFilled = hpSegments(hpTo, segments.length);
    const dying = Array.from(
      { length: Math.max(0, oldFilled - newFilled) },
      (_, index) => oldFilled - 1 - index,
    );
    const flashes =
      dying.length > 0 ? dying : oldFilled > 0 && hpTo < hpFrom ? [oldFilled - 1] : [];
    const slot = flashes.length > 0 ? duration / flashes.length : duration;

    for (const [index, segmentIndex] of flashes.entries()) {
      const segment = segments[segmentIndex];
      if (!segment) continue;
      const start = at + index * slot;
      timeline.call(() => segment.classList.add('fx-dying'), undefined, start);
      timeline.call(
        () => {
          segment.classList.remove('fx-dying');
          if (dying.includes(segmentIndex)) segment.classList.add('fx-dead');
        },
        undefined,
        start + Math.min(dur(0.045), slot * 0.5),
      );
    }

    timeline.call(
      () => {
        commit?.();
        queueMicrotask(() => this.clearSegments(panel));
      },
      undefined,
      at + duration,
    );
  }

  private clearSegments(panel: HTMLElement | null): void {
    for (const segment of panel?.querySelectorAll('.hpJ > i') ?? []) {
      segment.classList.remove('fx-dying', 'fx-dead');
    }
  }

  private targetPanel(side: CombatSide): HTMLElement | null {
    return this.view.querySelector<HTMLElement>(`[data-combat-player="${side}"] .pnJ`);
  }

  private sourceNode(id?: string): HTMLElement | null {
    if (!id) return null;
    return this.board.querySelector<HTMLElement>(`[data-entity-id="${CSS.escape(id)}"]`);
  }

  private geometry(source: HTMLElement | null, target: HTMLElement | null) {
    const rootRect = this.root.getBoundingClientRect();
    const boardRect = this.board.getBoundingClientRect();
    const sourceRect = source?.getBoundingClientRect() ?? boardRect;
    const targetRect = target?.getBoundingClientRect() ?? boardRect;
    const scoreRect =
      target?.querySelector<HTMLElement>('.scJ')?.getBoundingClientRect() ?? targetRect;
    const from = {
      x: sourceRect.left + sourceRect.width / 2 - rootRect.left,
      y: sourceRect.top + sourceRect.height / 2 - rootRect.top,
    };
    const to = {
      x: targetRect.left + targetRect.width / 2 - rootRect.left,
      y: targetRect.top + targetRect.height / 2 - rootRect.top,
    };
    return {
      from,
      to,
      damage: {
        x: scoreRect.left + scoreRect.width / 2 - rootRect.left,
        y: scoreRect.top + scoreRect.height / 2 - rootRect.top,
      },
      angle: (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI - 90,
      distance: Math.hypot(to.x - from.x, to.y - from.y),
    };
  }

  private place(node: CombatEffectNode, geometry: ReturnType<CombatEffects['geometry']>): void {
    node.beam.style.setProperty('--beam-length', `${geometry.distance}px`);
    gsap.set(node.beam, {
      x: geometry.from.x,
      y: geometry.from.y,
      rotation: geometry.angle,
      transformOrigin: '50% 0%',
    });
    gsap.set(node.impact, { x: geometry.to.x, y: geometry.to.y });
    gsap.set(node.damage, { x: geometry.damage.x, y: geometry.damage.y });
  }

  private unit(): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--u');
    return Number.parseFloat(raw) || window.innerWidth / 1080;
  }

  private reportPool(): void {
    const { free, busy } = this.pool.stats;
    metrics.setPool(free, busy);
  }
}

function createEffectNode(): CombatEffectNode {
  const node = document.createElement('div');
  node.className = 'combat-effect';

  const beam = document.createElement('div');
  beam.className = 'combat-beam';
  const core = document.createElement('i');
  core.className = 'combat-beam__core';
  beam.appendChild(core);
  for (let index = 0; index < 9; index += 1) {
    const block = document.createElement('i');
    block.className = `combat-beam__block combat-beam__block--${index}`;
    beam.appendChild(block);
  }

  const impact = document.createElement('div');
  impact.className = 'combat-impact';
  for (let index = 0; index < 13; index += 1) impact.appendChild(document.createElement('i'));

  const damage = document.createElement('div');
  damage.className = 'combat-damage';
  const ko = document.createElement('div');
  ko.className = 'combat-ko';
  ko.textContent = 'K.O.';

  node.append(beam, impact, damage, ko);
  return Object.assign(node, { beam, impact, damage, ko });
}

function prepareNode(
  node: CombatEffectNode,
  attacker: CombatSide,
  powerValue?: 512 | 1024 | 2048,
): void {
  node.className = `combat-effect combat-effect--active combat-effect--${attacker}${powerValue ? ' combat-effect--power' : ''}`;
  gsap.set([node.beam, node.impact, node.damage, node.ko], {
    clearProps: 'all',
  });
  gsap.set([node.beam, node.impact, node.damage, node.ko], {
    opacity: 0,
    scale: 1,
  });
}

function resetEffectNode(node: CombatEffectNode): void {
  node.className = 'combat-effect';
  node.beam.style.removeProperty('--beam-length');
  gsap.set([node.beam, node.impact, node.damage, node.ko], { clearProps: 'all' });
}

function hpSegments(hp: number, count: number): number {
  if (count === 0) return 0;
  return Math.round((Math.min(100, Math.max(0, hp)) / 100) * count);
}
